import { useEffect, useRef, useState } from 'react'

export interface TrackingProvider {
  track: (eventName: string, meta?: Record<string, any>) => void
}

const providers: TrackingProvider[] = []

let sessionInfo: Record<string, any> = {}

/**
 * Register a new analytics/event tracking provider
 */
export function registerTrackingProvider(provider: TrackingProvider): void {
  providers.push(provider)
}

export const setSessionInfo = (info: Record<string, any>): void => {
  sessionInfo = { ...sessionInfo, ...info }
}

export const updateSessionInfo = (key: string, value: any): void => {
  sessionInfo[key] = value
}

export const clearSessionInfo = (): void => {
  sessionInfo = {}
}

export const getSessionInfo = (): Record<string, any> => ({ ...sessionInfo })

export enum InputMethod {
  TYPING = 'typing',
  SPEECH_TO_TEXT = 'speech_to_text',
  SUGGESTED_PROMPT = 'suggested_prompt',
}

export enum TrackingEvent {
  AI_ASSISTANT_OPENED = 'AiAssistantOpened',
  AI_ASSISTANT_CLOSED = 'AiAssistantClosed',
  AI_ASSISTANT_MESSAGE_SENT = 'AiAssistantMessageSent',
  AI_ASSISTANT_SPEECH_TO_TEXT_COMPLETED = 'AiAssistantSpeechToTextCompleted',
  AI_ASSISTANT_RESPONSE_LINK_CLICKED = 'AiAssistantResponseLinkClicked',
  AI_ASSISTANT_SESSION_CREATED = 'AiAssistantSessionCreated',
  AI_ASSISTANT_SESSION_SWITCHED = 'AiAssistantSessionSwitched',
  AI_ASSISTANT_SESSION_DELETED = 'AiAssistantSessionDeleted',
  AI_ASSISTANT_RESPONSE_FIRST_TOKEN = 'AiAssistantResponseFirstToken',
  AI_ASSISTANT_RESPONSE_COMPLETE = 'AiAssistantResponseComplete',
  AI_ASSISTANT_FEEDBACK_POSITIVE = 'AiAssistantFeedbackPositive',
  AI_ASSISTANT_FEEDBACK_NEGATIVE = 'AiAssistantFeedbackNegative',
  CREATE_SOAP = 'CreateSoap',
  OPEN_EDIT_APPOINTMENT_DIALOG = 'OpenEditAppointmentDialog',
  OPEN_ADD_APPOINTMENT_DIALOG = 'OpenAddAppointmentDialog',
  CLICK_SAVE_APPOINTMENT = 'ClickSaveAppointment',
  APPOINTMENT_VALIDATION_ERROR = 'AppointmentValidationError',
  BYPASS_APPOINTMENT_VALIDATION = 'BypassAppointmentValidation',
  PATCH_APPOINTMENT_REQUEST = 'PatchAppointmentRequest',
  SEARCH_CHARGE_TAB = 'SearchChargeTab',
  SEARCH_CHARGE_RESULTS = 'SearchChargeResults',
  SEARCH_CHARGE_CLICK_RESULT = 'SearchChargeClickResult',
  SEARCH_CHARGE_NO_SELECTION = 'SearchChargeNoSelection',
  SESSION_INFO = 'SessionInfo',
  SWAP_PRODUCT_BUTTON = 'SwapProductButton',
  SWAP_COUNT_BUTTON = 'SwapCountButton',
  SWAP_PATIENT_BUTTON = 'SwapPatientButton',
  SWAP_PRODUCT_SEARCH_SELECT_PRODUCT = 'SwapProductSearchSelectProduct',
  SWAP_PRODUCT_SEARCH_CANCEL = 'SwapProductSearchCancel',
  SWAP_PRODUCT_SEARCH_CANCEL_GO_BACK = 'SwapProductSearchCancelGoBack',
  SWAP_PRODUCT_SEARCH_CANCEL_CLOSE = 'SwapProductSearchCancelClose',
  SWAP_PRODUCT_SEARCH_CANCEL_DISCARD = 'SwapProductSearchCancelDiscard',
  SWAP_PRODUCT_RX_FORM_APPROVE = 'SwapProductRxFormApprove',
  SWAP_PRODUCT_RX_FORM_CANCEL = 'SwapProductRxFormCancel',
  SWAP_PRODUCT_FINALIZE_CANCEL = 'SwapProductFinalizeCancel',
  SWAP_PRODUCT_FINALIZE_CONFIRM = 'SwapProductFinalizeConfirm',
  SWAP_COUNT_RX_FORM_APPROVE = 'SwapCountRxFormApprove',
  SWAP_COUNT_RX_FORM_CANCEL = 'SwapCountRxFormCancel',
  SWAP_COUNT_FINALIZE_CANCEL = 'SwapCountFinalizeCancel',
  SWAP_COUNT_FINALIZE_CONFIRM = 'SwapCountFinalizeConfirm',
  SWAP_PATIENT_SEARCH_CANCEL = 'SwapPatientSearchCancel',
  SWAP_PATIENT_SEARCH_SELECT_PATIENT = 'SwapPatientSearchSelectPatient',
  SWAP_PATIENT_RX_FORM_APPROVE = 'SwapPatientRxFormApprove',
  SWAP_PATIENT_RX_FORM_CANCEL = 'SwapPatientRxFormCancel',
  SWAP_PATIENT_FINALIZE_CANCEL = 'SwapPatientFinalizeCancel',
  SWAP_PATIENT_FINALIZE_CONFIRM = 'SwapPatientFinalizeConfirm',
  DECLINE_PET_NEEDS_SEEN = 'DeclinePetNeedsSeen',
  DECLINE_WRONG_PRODUCT = 'DeclineWrongProduct',
  DECLINE_WRONG_PET = 'DeclineWrongPet',
  DECLINE_NOT_A_CLINIC_PATIENT = 'DeclineNotAClinicPatient',
  DECLINE_OTHER = 'DeclineOther',
}

export enum SearchChargeNoSelectionReason {
  SEARCH_TERM_UPDATED = 'search_term_updated',
  SEARCH_TERM_CLEARED = 'search_term_cleared',
  NAVIGATED_AWAY = 'navigated_away',
}

/**
 * Track an event using all registered providers
 */
export const trackEvent = (
  eventName: TrackingEvent,
  meta: Record<string, any> = {},
): void => {
  // setTimeout ensures that the event tracking is not blocking the main thread
  setTimeout(() => {
    // Merge session info with event-specific meta, prefixing session keys with "session_"
    const enrichedMeta = {
      ...Object.fromEntries(
        Object.entries(sessionInfo).map(([key, value]) => [
          `session_${key}`,
          value,
        ]),
      ),
      ...meta,
    }

    providers.forEach((provider) => {
      try {
        provider.track(eventName, enrichedMeta)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`Tracking error for ${eventName}:`, err)
      }
    })
  }, 0)
}

/**
 * Tracks an event only once when the component mounts.
 */
export const useTrackMount = (
  eventName: TrackingEvent,
  meta: Record<string, any> = {},
): void => {
  const hasTrackedRef = useRef(false)

  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackEvent(eventName, meta)
      hasTrackedRef.current = true
    }
  }, [eventName, meta])
}

export const useTrackSearch = (
  eventName: TrackingEvent,
  selectedItemKey: string,
): {
  addSearchAttempt: (searchAttempt: string) => void
  startSearch: () => void
  trackSearch: (
    selectedItem: string | undefined,
    meta?: Record<string, any>,
  ) => void
} => {
  const [searchAttempts, setSearchAttempts] = useState<string[]>([])
  const [searchStarted, setSearchStarted] = useState<Date | null>(null)

  const startSearch = () => {
    if (searchStarted === null) {
      setSearchStarted(new Date())
    }
  }

  const addSearchAttempt = (searchAttempt: string) => {
    startSearch()
    setSearchAttempts((prev) => [...prev, searchAttempt])
  }

  const trackSearch = (
    selectedItem: string | undefined,
    meta: Record<string, any> = {},
  ) => {
    trackEvent(eventName, {
      [selectedItemKey]: selectedItem,
      searchAttempts,
      searchDuration: searchStarted
        ? new Date().getTime() - searchStarted.getTime()
        : undefined,
      ...meta,
    })
    setSearchAttempts([])
    setSearchStarted(null)
  }

  return {
    startSearch,
    addSearchAttempt,
    trackSearch,
  }
}
